/**
 * Estado e consulta previa do limite de horas consecutivas na unidade.
 * Usado pelos modais de alocacao do painel: consulta o preview antes do submit,
 * guarda as violacoes para o bloco inline e traduz o erro 400 do submit.
 *
 * O preview NAO e o guard: se ele falhar, o fluxo segue e o backend revalida
 * a mesma regra dentro da transacao de escrita.
 */
import { ref, computed } from 'vue'
import Network from '@/Network'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { extractConsecutiveHoursViolations } from '@/utils/consecutiveHoursErrors'
import type { ConsecutiveHoursCheckCandidate } from '@/Network/Types/Requests/StationRequests'
import type { ConsecutiveHoursViolation } from '@/Network/Types/Responses/ConsecutiveHoursResponses'

export function useConsecutiveHoursCheck() {
  const { flags } = useFeatureFlags()
  const violations = ref<ConsecutiveHoursViolation[]>([])
  const isChecking = ref(false)

  const hasViolations = computed(() => violations.value.length > 0)

  function clearViolations() {
    violations.value = []
  }

  /** `true` quando a operação pode seguir (sem violação, flag desligada ou preview indisponível). */
  async function runCheck(candidates: ConsecutiveHoursCheckCandidate[]): Promise<boolean> {
    clearViolations()
    if (!flags.enabledConsecutiveHoursLimit || candidates.length === 0) return true

    isChecking.value = true
    try {
      const result = await Network.station.consecutiveHoursCheck(candidates)
      violations.value = result.violations ?? []
      return violations.value.length === 0
    } catch (error) {
      console.error('Falha ao consultar o limite de horas consecutivas:', error)
      return true
    } finally {
      isChecking.value = false
    }
  }

  /** `true` quando o erro era o bloqueio da regra (e o bloco inline foi preenchido). */
  function applyErrorViolations(error: unknown): boolean {
    const parsed = extractConsecutiveHoursViolations(error)
    if (!parsed) return false
    violations.value = parsed
    return true
  }

  return { violations, isChecking, hasViolations, runCheck, applyErrorViolations, clearViolations }
}
